import React, { useImperativeHandle, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { CheckboxProps } from "./CheckboxProps";
import classes from "./Checkbox.module.scss";
import { Check } from "@/components/ui/Icon/Check";
import { Bar } from "@/components/ui/Icon/Bar";

export type CheckboxComponent = React.ForwardRefExoticComponent<
  CheckboxProps & React.RefAttributes<HTMLInputElement>
>;

export const Checkbox: CheckboxComponent = React.forwardRef<
  HTMLInputElement,
  CheckboxProps
>(function Checkbox(props, ref) {
  const { indeterminate, className, checked, children, ...htmlProps } = props;
  const checkboxRef = React.useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => checkboxRef.current!);

  useEffect(() => {
    if (!checkboxRef.current) return;

    if (indeterminate) {
      checkboxRef.current.indeterminate = true;
      checkboxRef.current.checked = false;
    } else if (checked) {
      checkboxRef.current.indeterminate = false;
      checkboxRef.current.checked = true;
    } else {
      checkboxRef.current.indeterminate = false;
      checkboxRef.current.checked = false;
    }
  }, [indeterminate, checked]);

  const handleClick = useCallback((ev: React.MouseEvent<HTMLLabelElement>) => {
    ev.stopPropagation();
  }, []);

  return (
    <label onClick={handleClick} className={clsx(classes.base, className)}>
      <input
        className={classes.input}
        type="checkbox"
        ref={checkboxRef}
        {...htmlProps}
      />

      <span aria-hidden="true" className={classes.boxIcon}>
        {indeterminate ? (
          <Bar strokeWidth={3.5} />
        ) : (
          checked && <Check strokeWidth={3.5} />
        )}
      </span>

      {children && <span>{children}</span>}
    </label>
  );
});
